import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { moeda } from '../funcoes';
import api from '../../services/api';
import { toastInfo, toastError } from '../../toasts'
import location from '../../assets/icons/location1.png';
import CardProduto from '../CardProduto';
import { CustomButton } from '../CustomButton';
import { CustomSelectPicker } from '../CustomSelectPicker';
import { LoadingSpinner } from '../LoadingSpinner';
import * as S from './styles';
import { getUserCancel } from '../../services/user/getUserCancel';
import { useConfig } from '../../hooks/config';
import { IMAGES_PATH } from '../../utils/imagesPath';

const deliveryTypeLabels = {
  LOCAL: 'Consumir no local',
  EN: 'Entrega',
  AG: 'Agendamento',
  AGRE: 'Agendamento',
  RE: 'Retirar na loja',
};

const DetalhesPedido = ({
  onClose,
  pedido,
  entregadores,
  refresh,
  formatarData,
}) => {
  const [pedidoItens, setPedidoItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pedidoStatus, setPedidoStatus] = useState('');
  const [pedidoEntregador, setPedidoEntregador] = useState(0);
  const [enderecoCliente, setEnderecoCliente] = useState({});
  const { config } = useConfig();
  const [userCancel, setUserCancel] = useState(null);

  const vizualizarComprovante = (imagem) => {
    window.open(IMAGES_PATH + imagem, 'Comprovante', 'height=600,width=600');
  };

  const statusOptions = ['PENDENTE', 'ANDAMENTO', 'ENTREGA', 'CONCLUIDO'].map(
    (item) => ({ label: item, value: item }),
  );

  const entregadorOptions = entregadores.map((item) => ({
    label: item.Nome,
    value: item.Id,
  }));

  const preencherEndereco = (pedido) => {
    if (
      pedido.IdEndereco === null &&
      ['EN', 'AG'].includes(pedido.TipoEntrega)
    ) {
      setEnderecoCliente({
        Cep: '',
        Logradouro: pedido.EnderecoSemCadastro,
        Numero: '',
        Complemento: '',
        PontoRef: '',
        Bairro: pedido.NomeBairroSemCadastro,
        Cidade: pedido.Cidade,
        Uf: pedido.Uf,
      });
    } else {
      setEnderecoCliente({
        Cep: pedido.Cep,
        Logradouro: pedido.Logradouro,
        Numero: pedido.Numero,
        Complemento: pedido.Complemento,
        PontoRef: pedido.PontoRef,
        Bairro: pedido.Bairro,
        Cidade: pedido.Cidade,
        Uf: pedido.Uf,
      });
    }
  };

  const comecarProducao = async () => {
    if (config?.DisableOrdersActions) {
      toastError('Ações de pedidos estão desabilitadas');
      return;
    }

    let Tipo = pedido.TipoEntrega === 'AG' ? 'EN' : 'RE';
    await api.post('pedido/alterarTipoEntrega', {
      TipoEntrega: Tipo,
      Id: pedido.Id,
    });
    toastInfo('Pedido aceito!');
    refresh();
    onClose();
  };

  const findUserCancel = useCallback(() => {
    if (pedido.UserCancelamento) {
      getUserCancel(pedido.UserCancelamento).then((user) => {
        if (user) setUserCancel(user);
      });
    }
  }, [pedido?.UserCancelamento]);

  useEffect(() => {
    if (pedidoEntregador !== 0) {
      localStorage.setItem('pedidoEntregador', pedidoEntregador);
    }
    if (pedidoStatus !== '') {
      localStorage.setItem('pedidoStatus', pedidoStatus);
    }
  }, [pedidoEntregador, pedidoStatus]);

  useEffect(() => {
    api
      .get(`pedido-produto/encontrar/${pedido.Id}`)
      .then((res) => {
        setPedidoItens(res.data);
        setPedidoStatus(pedido.Status);
        setPedidoEntregador(pedido.IdEntregador);
        setLoading(false);
      })
      .catch((error) => console.log(error));

    findUserCancel();
    preencherEndereco(pedido);
  }, [findUserCancel, pedido]);

  const customerName = pedido.IdUser
    ? pedido.Nome
    : pedido.NomeSemCadastro;
  const customerPhone = pedido.IdUser
    ? pedido.Telefone
    : pedido.CelularSemCadastro;
  const trocoValue = parseFloat(pedido.Troco) || 0;
  // Desconto já inclui o valor do benefício de fidelidade utilizado (exceto
  // frete grátis, que zera TaxaEntrega em vez de virar um desconto) — não
  // subtrair DescontoFidelidade de novo aqui, senão o benefício é descontado
  // duas vezes do total exibido. DescontoFidelidade ainda é usado abaixo só
  // para exibir a linha informativa "Cashback resgatado".
  const totalCalc =
    parseFloat(pedido.ValorTotal) +
    parseFloat(pedido.TaxaAdministracao ?? 0) +
    parseFloat(pedido.TaxaEntrega) -
    parseFloat(pedido.Desconto ?? 0);
  const hasAddress =
    enderecoCliente.Cep !== undefined && enderecoCliente.Cep !== null;
  const isSchedule =
    pedido.TipoEntrega === 'AG' || pedido.TipoEntrega === 'AGRE';

  return (
    <S.Container>
      {/* ---- Info do Pedido ---- */}
      <S.Section>
        <S.SectionTitle>Informações</S.SectionTitle>
        <S.InfoGrid>
          <S.InfoItem>
            <S.InfoLabel>Cliente</S.InfoLabel>
            <S.InfoValue>{customerName}</S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel>Telefone</S.InfoLabel>
            <S.InfoValue>{customerPhone}</S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel>Data</S.InfoLabel>
            <S.InfoValue>
              {moment(pedido.DataPedido).format('DD/MM/YYYY')} às {pedido.Hora}
            </S.InfoValue>
          </S.InfoItem>
          <S.InfoItem>
            <S.InfoLabel>Tipo</S.InfoLabel>
            <S.InfoValue>
              {deliveryTypeLabels[pedido.TipoEntrega] || pedido.TipoEntrega}
            </S.InfoValue>
          </S.InfoItem>
          {pedido.FormaPagamento && (
            <S.InfoItem>
              <S.InfoLabel>Pagamento</S.InfoLabel>
              <S.InfoValue>{pedido.FormaPagamento}</S.InfoValue>
            </S.InfoItem>
          )}
          {trocoValue > 0 && (
            <>
              <S.InfoItem>
                <S.InfoLabel>Troco para</S.InfoLabel>
                <S.InfoValue>{moeda(pedido.Troco)}</S.InfoValue>
              </S.InfoItem>
              <S.InfoItem>
                <S.InfoLabel>Troco do cliente</S.InfoLabel>
                <S.InfoValue>
                  {moeda(trocoValue - parseFloat(pedido.ValorTotal) - parseFloat(pedido.TaxaEntrega))}
                </S.InfoValue>
              </S.InfoItem>
            </>
          )}
          {pedido?.FormaPag === 99 && pedido?.AsaasPaymentId && (
            <S.InfoItem>
              <S.InfoLabel>Pagamento online</S.InfoLabel>
              <S.ConfirmPayment confirm={pedido?.AsaasConfirmPayment}>
                {pedido?.AsaasConfirmPayment ? 'Confirmado' : 'Aguardando'}
              </S.ConfirmPayment>
            </S.InfoItem>
          )}
          {pedido?.Status === 'CANCELADO' && userCancel?.Id && (
            <S.InfoItem>
              <S.InfoLabel>Cancelado por</S.InfoLabel>
              <S.InfoValue>
                {userCancel?.Nome} - {userCancel?.IdEmpresa || userCancel?.Telefone}
              </S.InfoValue>
            </S.InfoItem>
          )}
        </S.InfoGrid>

        {pedido.Comprovante !== null && (
          <div style={{ marginTop: 10 }}>
            <CustomButton
              size="sm"
              onClick={() => vizualizarComprovante(pedido.Comprovante)}
            >
              Ver Comprovante
            </CustomButton>
          </div>
        )}

        {pedido.DataAgendamento !== null && (
          <S.InfoItem style={{ marginTop: 10 }}>
            <S.InfoLabel>Data Agendada</S.InfoLabel>
            <S.InfoValue>{formatarData(pedido.DataAgendamento)}</S.InfoValue>
          </S.InfoItem>
        )}
      </S.Section>

      {/* ---- Endereço ---- */}
      {hasAddress && (
        <S.Section>
          <S.SectionTitle>Endereço</S.SectionTitle>
          <S.AddressCard>
            <img alt="Localização" src={location} />
            <span>
              {enderecoCliente.Logradouro}
              {enderecoCliente.Numero ? `, ${enderecoCliente.Numero}` : ''}
              {enderecoCliente.Complemento ? ` - ${enderecoCliente.Complemento}` : ''}
              {enderecoCliente.Bairro ? `, ${enderecoCliente.Bairro}` : ''}
              {enderecoCliente.Cidade ? ` - ${enderecoCliente.Cidade}` : ''}
              {enderecoCliente.Uf ? `/${enderecoCliente.Uf}` : ''}
              {enderecoCliente.Cep ? ` (${enderecoCliente.Cep})` : ''}
              {enderecoCliente.PontoRef ? ` · Ref: ${enderecoCliente.PontoRef}` : ''}
            </span>
          </S.AddressCard>
        </S.Section>
      )}

      {/* ---- Ações: Status / Entregador ---- */}
      <S.Section>
        {pedido.DataAgendamento && isSchedule ? (
          <CustomButton onClick={() => comecarProducao()}>
            Aceitar agendamento
          </CustomButton>
        ) : (
          <S.SelectRow>
            <S.SelectGroup>
              <label>Status</label>
              <CustomSelectPicker
                data={statusOptions}
                style={{ width: '100%' }}
                defaultValue={pedido.Status}
                onChange={setPedidoStatus}
                searchable={false}
              />
            </S.SelectGroup>
            <S.SelectGroup>
              <label>Entregador</label>
              <CustomSelectPicker
                data={entregadorOptions}
                style={{ width: '100%' }}
                defaultValue={pedido.IdEntregador}
                onChange={setPedidoEntregador}
              />
            </S.SelectGroup>
          </S.SelectRow>
        )}
      </S.Section>

      {/* ---- Produtos ---- */}
      <S.Section>
        <S.SectionTitle>Produtos</S.SectionTitle>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <LoadingSpinner />
          </div>
        ) : (
          <S.ProductsList>
            {pedidoItens.map((pro) => (
              <li key={pro.Id}>
                <CardProduto idPedido={pro.Id} data={pro} />
              </li>
            ))}
          </S.ProductsList>
        )}
      </S.Section>

      {/* ---- Totais ---- */}
      {!loading && (
        <S.TotalsCard>
          <S.TotalRow $muted>
            <span>Subtotal</span>
            <span>{moeda(pedido.ValorTotal)}</span>
          </S.TotalRow>
          {(() => {
            // Desconto já inclui o valor da fidelidade (ver totalCalc acima)
            // — exibe só a parte de cupom/manual aqui pra não somar visualmente
            // com a linha "Cashback resgatado" abaixo e parecer um desconto
            // maior do que o que realmente saiu do total.
            const descontoSemFidelidade =
              parseFloat(pedido.Desconto ?? 0) -
              parseFloat(pedido.DescontoFidelidade ?? 0);
            return (
              descontoSemFidelidade > 0 && (
                <S.TotalRow $green>
                  <span>Desconto</span>
                  <span>- {moeda(descontoSemFidelidade)}</span>
                </S.TotalRow>
              )
            );
          })()}
          {parseFloat(pedido.DescontoFidelidade) > 0 && (
            <S.TotalRow $green>
              <span>Cashback resgatado</span>
              <span>- {moeda(pedido.DescontoFidelidade)}</span>
            </S.TotalRow>
          )}
          {parseFloat(pedido.TaxaEntrega) > 0 && (
            <S.TotalRow $muted>
              <span>Taxa de entrega</span>
              <span>{moeda(pedido.TaxaEntrega)}</span>
            </S.TotalRow>
          )}
          {parseFloat(pedido.TaxaAdministracao) > 0 && (
            <S.TotalRow $muted>
              <span>Taxa administrativa</span>
              <span>{moeda(pedido.TaxaAdministracao)}</span>
            </S.TotalRow>
          )}
          <S.TotalDivider />
          <S.TotalRow $bold>
            <span>Total</span>
            <span>{moeda(totalCalc)}</span>
          </S.TotalRow>
        </S.TotalsCard>
      )}
    </S.Container>
  );
};

export default DetalhesPedido;
